/**
 * The typed codec for the dashboard's URL state (reservation-dashboard-ui Tech-decision "View
 * architecture"): view + filters live entirely in the query string, so every filtered view is
 * deep-linkable and switching views never refetches a different dataset — it only re-presents.
 *
 * It also owns the NARROWING GUARD (ADR-0022 wide-range edge case): on an unbounded query — no date
 * range, no status, no strong identifier — the list refuses to fire and prompts the user to narrow,
 * so a busy multi-venue account never triggers a full-table scan.
 */
import {
  BOOKING_CHANNELS,
  BOOKING_STATUSES,
  type BookingChannel,
  type BookingStatus,
} from "./status";

export const BOOKING_VIEWS = ["list", "calendar", "timeline", "floor", "inbox"] as const;
export type BookingView = (typeof BOOKING_VIEWS)[number];
export const DEFAULT_VIEW: BookingView = "list";

export interface BookingFilter {
  /** Accent-insensitive name/text search (server-side). */
  q?: string;
  phone?: string;
  email?: string;
  /** Service-day range, ISO `yyyy-MM-dd`. */
  from?: string;
  to?: string;
  status: BookingStatus[];
  channel: BookingChannel[];
  partyMin?: number;
  partyMax?: number;
  zoneId?: string;
}

export const EMPTY_FILTER: BookingFilter = { status: [], channel: [] };

function toView(raw: string | null): BookingView {
  return (BOOKING_VIEWS as readonly string[]).includes(raw ?? "")
    ? (raw as BookingView)
    : DEFAULT_VIEW;
}

function num(raw: string | null): number | undefined {
  if (raw === null || raw.trim() === "") return undefined;
  const n = Number(raw);
  return Number.isFinite(n) ? n : undefined;
}

function str(raw: string | null): string | undefined {
  const v = raw?.trim();
  return v ? v : undefined;
}

/** The `?day=` anchor (timeline/floor) — a single service day; defaults handled by the caller. */
export function parseDay(sp: URLSearchParams): string | undefined {
  return str(sp.get("day"));
}

export function parseView(sp: URLSearchParams): BookingView {
  return toView(sp.get("view"));
}

export function parseFilter(sp: URLSearchParams): BookingFilter {
  const statusSet = new Set(BOOKING_STATUSES as readonly string[]);
  const channelSet = new Set(BOOKING_CHANNELS as readonly string[]);
  return {
    q: str(sp.get("q")),
    phone: str(sp.get("phone")),
    email: str(sp.get("email")),
    from: str(sp.get("from")),
    to: str(sp.get("to")),
    status: sp.getAll("status").filter((s) => statusSet.has(s)) as BookingStatus[],
    channel: sp.getAll("channel").filter((c) => channelSet.has(c)) as BookingChannel[],
    partyMin: num(sp.get("partyMin")),
    partyMax: num(sp.get("partyMax")),
    zoneId: str(sp.get("zoneId")),
  };
}

/** Serialize view + filter (+ optional day) back to query params — the deep-link writer. */
export function toSearch(view: BookingView, filter: BookingFilter, day?: string): URLSearchParams {
  const sp = new URLSearchParams();
  if (view !== DEFAULT_VIEW) sp.set("view", view);
  if (filter.q) sp.set("q", filter.q);
  if (filter.phone) sp.set("phone", filter.phone);
  if (filter.email) sp.set("email", filter.email);
  if (filter.from) sp.set("from", filter.from);
  if (filter.to) sp.set("to", filter.to);
  for (const s of filter.status) sp.append("status", s);
  for (const c of filter.channel) sp.append("channel", c);
  if (filter.partyMin !== undefined) sp.set("partyMin", String(filter.partyMin));
  if (filter.partyMax !== undefined) sp.set("partyMax", String(filter.partyMax));
  if (filter.zoneId) sp.set("zoneId", filter.zoneId);
  if (day) sp.set("day", day);
  return sp;
}

/**
 * Whether the filter is narrow enough to fire an unbounded LIST query (ADR-0022). A date range, any
 * status, or a strong identifier (name/phone/email) all count; an empty filter does NOT — the list
 * then prompts to narrow instead of scanning. Calendar/timeline/floor are day-bounded by their own
 * axis, so they don't consult this.
 */
export function isNarrowed(filter: BookingFilter): boolean {
  return Boolean(
    filter.from ||
    filter.to ||
    filter.status.length > 0 ||
    filter.q ||
    filter.phone ||
    filter.email,
  );
}

/** A stable, order-independent cache-key fragment for a filter (drives the query key). */
export function filterKey(filter: BookingFilter): string {
  return JSON.stringify({
    q: filter.q ?? null,
    phone: filter.phone ?? null,
    email: filter.email ?? null,
    from: filter.from ?? null,
    to: filter.to ?? null,
    status: [...filter.status].toSorted(),
    channel: [...filter.channel].toSorted(),
    partyMin: filter.partyMin ?? null,
    partyMax: filter.partyMax ?? null,
    zoneId: filter.zoneId ?? null,
  });
}
